import { type ComponentType, type ReactNode, createContext, useCallback, useContext, useMemo } from "react";

/**
 * MUI Pro components context.
 *
 * When MUI X Pro is available (license key set + pro packages installed),
 * components can retrieve upgraded Pro variants (e.g., DataGridPro instead of DataGrid)
 * via the `getComponent` helper.
 */
export interface MuiProContextType {
	/** Whether MUI Pro features are enabled (license key present and activated). */
	isProEnabled: boolean;
	/**
	 * Retrieve a MUI Pro component by name.
	 * Returns `null` if Pro is not enabled or the component was not registered.
	 *
	 * Known component names:
	 * - `"DataGrid"` → `DataGridPro` from `@mui/x-data-grid-pro`
	 */
	getComponent: (name: string) => ComponentType<any> | null;
}

const MuiProContext = createContext<MuiProContextType | null>(null);

export interface MuiProProviderProps {
	/** Whether MUI Pro is enabled. */
	isProEnabled: boolean;
	/**
	 * Map of Pro component names to actual component references.
	 * Populated during app initialization via dynamic import.
	 *
	 * Example: `{ DataGrid: DataGridPro }`
	 */
	components?: Record<string, ComponentType<any>>;
	children: ReactNode;
}

/**
 * Provider for MUI Pro components.
 *
 * Typically wrapped around the application by JudoRuntime when a `muiProLicenseKey`
 * is configured. Components use `useMuiPro()` / `useMuiProOptional()` to access
 * Pro-upgraded components.
 */
export function MuiProProvider({ isProEnabled, components = {}, children }: MuiProProviderProps) {
	const getComponent = useCallback(
		(name: string): ComponentType<any> | null => {
			if (!isProEnabled) return null;
			return components[name] ?? null;
		},
		[isProEnabled, components]
	);

	const value = useMemo<MuiProContextType>(() => ({ isProEnabled, getComponent }), [isProEnabled, getComponent]);

	return <MuiProContext.Provider value={value}>{children}</MuiProContext.Provider>;
}

/**
 * Hook to access MUI Pro context.
 * Throws if used outside MuiProProvider.
 */
export function useMuiPro(): MuiProContextType {
	const context = useContext(MuiProContext);
	if (!context) {
		throw new Error("useMuiPro must be used within MuiProProvider");
	}
	return context;
}

/**
 * Hook to access MUI Pro context (optional).
 * Returns `null` if used outside MuiProProvider.
 * Preferred in library components that should work with or without Pro.
 */
export function useMuiProOptional(): MuiProContextType | null {
	return useContext(MuiProContext);
}
